import { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import type { DayWorkStatus, DayWorkStatusKind } from "../types/dashboard2-work-status.types";
import { deriveDayWorkStatusForUser } from "../utils/dashboard2-schedule.utils";

export type Dashboard2ColleagueWithDayStatus = {
  colleague: TeamMember;
  status: DayWorkStatus;
};

const VISIBLE_COLLEAGUE_STATUS_KINDS = new Set<DayWorkStatusKind>([
  "office",
  "mobile_working",
  "other_bookings"
]);

export function isDashboard2VisibleColleagueStatus(kind: DayWorkStatusKind): boolean {
  return VISIBLE_COLLEAGUE_STATUS_KINDS.has(kind);
}

export function useDashboard2VisibleColleaguesToday(
  schedules: BookingScheduleInterface[],
  storedColleagues: TeamMember[]
): Dashboard2ColleagueWithDayStatus[] {
  const { t } = useTranslation();
  const { timeFormat } = useTimeFormat();

  return useMemo(() => {
    const colleaguesWithStatus = storedColleagues.map(colleague => ({
      colleague,
      status: deriveDayWorkStatusForUser(schedules, colleague.userId, DateTime.now(), {
        mobileWorkingLabel: t("_dashboard2_statusMobileWorking"),
        notSetLabel: t("_dashboard2_statusNotSet"),
        timeFormat
      })
    }));

    return colleaguesWithStatus.filter(entry =>
      isDashboard2VisibleColleagueStatus(entry.status.kind)
    );
  }, [schedules, storedColleagues, t, timeFormat]);
}
